import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import type Highcharts from 'highcharts';
import { NivusService } from '../../sensoren/services/nivus.service';
import { ISensorenDashboardFlowData, ISensorenDashboardFlowSeriesData } from '../../sensoren/models/dashboard';

@Component({
  selector: 'app-niederschlag-widget',
  templateUrl: './niederschlag-widget.component.html',
  styleUrls: ['./niederschlag-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NiederschlagWidgetComponent {
  @Output() isLoading = new EventEmitter<boolean>(true);
  chartOptions: Highcharts.Options = {};
  hasNoData = false;
  daysBeforeWeek: number = 7;
  // Only daily totals above this get a bar; smaller ones (sensor noise) are shown as 0 mm.
  readonly minRainfall = 0.05;

  constructor(private nivusService: NivusService) {
    this.isLoading.emit(true);
    const until = new Date();
    const from = new Date(until.getFullYear(), until.getMonth(), until.getDate() - this.daysBeforeWeek);

    this.nivusService
      .getTotalRainfall(this.toLocalISOString(from), this.toLocalISOString(until))
      .subscribe((totalRainfall: ISensorenDashboardFlowData) => {
        const days = this.groupByDay(totalRainfall?.series ?? [], from, until);
        const categories = days.map((d) => this.getDateLabel(d.date));
        const values = days.map((d) => (d.value !== null && d.value <= this.minRainfall ? 0 : d.value));
        this.hasNoData = values.every((v) => v === null);
        this.buildChart(categories, values, totalRainfall?.unit ?? 'mm');
        this.isLoading.emit(false);
      });
  }

  // Sums the backend buckets per local calendar day; days without any reading stay null.
  private groupByDay(
    series: ISensorenDashboardFlowSeriesData[],
    from: Date,
    until: Date
  ): { date: Date; value: number | null }[] {
    const days = new Map<string, { date: Date; value: number | null }>();
    for (const day = new Date(from); day <= until; day.setDate(day.getDate() + 1)) {
      days.set(this.toDayKey(day), { date: new Date(day), value: null });
    }

    for (const point of series) {
      if (point.value === null || point.value === undefined) continue;
      const day = days.get(this.toDayKey(new Date(point.timestamp)));
      if (day) day.value = (day.value ?? 0) + point.value;
    }

    return [...days.values()];
  }

  private toDayKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  private toLocalISOString(date: Date): string {
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const pad = (n: number) => String(Math.abs(n)).padStart(2, '0');

    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:00:00` +
      `${sign}${pad(Math.floor(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`
    );
  }

  private getDateLabel(date: Date): string {
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }

  private buildChart(categories: string[], data: (number | null)[], unit: string): void {
    const COLOR_UP = '#9AC0A6';
    const COLOR_DOWN = '#FF5B41';

    const points = data.map((value, i) => {
      const prev = i > 0 ? data[i - 1] : null;
      return {
        y: value,
        // minPointLength would otherwise draw a stub for 0 mm days.
        color: value === 0 ? 'transparent' : prev === null || value === null || value >= prev ? COLOR_UP : COLOR_DOWN,
      };
    });

    this.chartOptions = {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        style: { fontFamily: '"IP Klartext", Arial, sans-serif' },
        margin: [10, 16, 45, 50],
        animation: false,
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories,
        tickLength: 0,
        lineWidth: 0,
        labels: {
          style: { fontSize: '10px', color: '#8D8E90', fontFamily: '"IP Klartext", Arial, sans-serif' },
        },
      },
      yAxis: {
        title: { text: undefined },
        tickInterval: 2,
        labels: {
          format: `{value} ${unit}`,
          style: { fontSize: '10px', color: '#8D8E90', fontFamily: '"IP Klartext", Arial, sans-serif', whiteSpace: 'nowrap' },
        },
        gridLineColor: '#f0ede6',
      },
      tooltip: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter(this: any) {
          const label = (this.key as string).replace(/\.$/, '');
          return `${label}: <b>${(this.y as number).toFixed(2)}</b> ${unit}`;
        },
        style: { fontFamily: '"IP Klartext", Arial, sans-serif', fontSize: '12px' },
        backgroundColor: '#fff',
        borderRadius: 8,
        borderWidth: 0,
        shadow: false,
      },
      plotOptions: {
        column: {
          pointWidth: 12,
          // Keeps days with very little rain visible next to a heavy-rain day.
          minPointLength: 3,
          borderWidth: 0,
          borderRadius: { radius: 85, where: 'all' } as unknown as number,
        },
      },
      series: [
        {
          type: 'column',
          data: points,
        },
      ],
    };
  }
}
